import { useUnit } from 'effector-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { $notificationStore, $profileStore, $updateCustomerDetailsStore } from '@lib/store';
import { cn, PaletteUtils } from '@shared/utils';
import { EnrolmentConfirmedModal } from './enrolmentConfirmedModal';
import { BaseInput, Button, Spinner, Text } from '@shared/widgets';

interface EnrolFormData {
  travelServicesEnrollmentContactName: string;
}
const DEFAULT_VALUES: EnrolFormData = { travelServicesEnrollmentContactName: '' };

interface EnrolProps {
  className?: string;
}

export const Enrol = (props: EnrolProps) => {
  const profileStore = useUnit($profileStore.store);

  const [showEnrolmentConfirmedModal, setShowEnrolmentConfirmedModal] = useState<boolean>(false);
  const { control, formState, handleSubmit, reset } = useForm<EnrolFormData>({ defaultValues: DEFAULT_VALUES });

  const { t } = useTranslation('travel');

  const submitHandler = async (data: EnrolFormData) => {
    const response = await $updateCustomerDetailsStore.events.updateCustomerDetails({
      travelServicesEnrollment: true,
      travelServicesEnrollmentContactName: data.travelServicesEnrollmentContactName,
    });
    if (response.success) {
      const response = await $profileStore.events.getProfile();
      if (!response.success) {
        $notificationStore.events.setError('Something went wrong while fetching Profile. Please try again.');
        return;
      }
      setShowEnrolmentConfirmedModal(true);
      reset(DEFAULT_VALUES);
    }
  };

  return (
    <section
      className={cn(
        'flex w-full max-w-[750px] flex-col gap-6 bg-white p-6 dark:bg-soft-black',
        PaletteUtils.isVcfcPalette && 'rounded-xl dark:border dark:border-gray-black',
        props.className,
      )}
    >
      <Spinner show={formState.isSubmitting} position />
      <div className='flex flex-col gap-1'>
        <Text tag='h2' weight='semibold' size='2xl'>
          {t('enrol.title')}
        </Text>
        <Text tag='p' color='gray'>
          {t('enrol.description')}
        </Text>
      </div>
      <form className='flex w-full flex-col gap-4' noValidate onSubmit={handleSubmit(submitHandler)}>
        <BaseInput label={t('enrol.labels.company')} readOnly value={profileStore.data?.companyName || ''} />
        <Controller
          name='travelServicesEnrollmentContactName'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <BaseInput
              {...field}
              label={t('enrol.labels.primaryTravelContact')}
              placeholder={t('placeholders.fullName', { ns: 'common' })}
              error={error?.message}
            />
          )}
        />
        <div className={cn('flex gap-4', 'max-md:flex-col')}>
          <BaseInput label={t('placeholders.email', { ns: 'common' })} readOnly value={profileStore.data?.emailAddress || ''} />
          <BaseInput
            label={t('enrol.labels.telephone')}
            readOnly
            value={`+${profileStore.data?.phoneAreaCode || ''}${profileStore.data?.phoneNumber || ''}`}
          />
        </div>
        <Button color='primary' type='submit' disabled={formState.isSubmitting}>
          {t('enrol.button')}
        </Button>
      </form>
      <EnrolmentConfirmedModal isOpen={showEnrolmentConfirmedModal} onClose={() => setShowEnrolmentConfirmedModal(false)} />
    </section>
  );
};
